import React, { Fragment, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import { addClass, removeClass } from 'app/utils/domHelpers';
import { FormattedMessage } from 'react-intl';
import { showError } from 'app/globalMessages/globalMessagesClient';
import useIsDesktop from 'app/utils/hooks/useIsDesktop';
import {
  STATUS_CODES,
  ATB_STATE,
  OUT_OF_STOCK_ERROR_MESSAGE_ID,
} from '../../commonComponents/addProduct/addProductConstants';
import ConfirmationView from './confirmationView';
import { useAddToBag } from '../../commonComponents/addProduct/addProductHooks';
import AddProductButton from '../../commonComponents/addProduct/addProductButton';
import { isMultiVariantProduct, scrollToSizeSelector } from '../productDetailsUtils';

const { baseCartUrl } = window.inlineCartConfiguration;

// same appearance rules as the cart sticky checkout bar, so the UX stays consistent between the two
const HEADER_FALLBACK = 72;
const APPEAR_OFFSET = 10;
const HYSTERESIS = 40;
const CTA_SELECTOR = '.product__add-to-bag-button';
const BOTTOM_BAR_VISIBLE_CLASS = 'is-pdp-sticky-add-to-bag-visible';
const BOTTOM_BAR_HEIGHT_VARIABLE = '--pdp-sticky-add-to-bag-height';

function StickyAddToBagWidget({
  isNotificationModalOpen,
  detailsPage,
  mainCtaRef,
  onNotifyAction,
  onSelectSizeRequired,
}) {
  const { isDesktop } = useIsDesktop();
  const [isVisible, setIsVisible] = useState(false);
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const [cartData, setCartData] = useState(null);
  const backdropRef = useRef(null);
  const overlayRef = useRef(null);
  const stickyBarRef = useRef(null);

  const { loadedProduct, selectedSize, hasStock, productType, disableNotifyMe } = detailsPage;

  const { addToBagState, addToBag } = useAddToBag({
    selectedSize,
    hasStock,
    product: loadedProduct,
    location: 'product',
    disableNotifyMe,
  });

  const fadeSpeed = 200;

  useEffect(() => {
    if (isDesktop) {
      setIsVisible(false);
      return undefined;
    }

    const header = document.querySelector('.header');
    let isCtaVisible = true;

    const update = () => {
      const section = mainCtaRef && mainCtaRef.current;

      if (!section) {
        return;
      }

      const cta = section.querySelector(CTA_SELECTOR) || section;
      const { bottom, height } = cta.getBoundingClientRect();

      if (!height) {
        return;
      }

      // the CTA counts as hidden as soon as it slides under the sticky header, not only when it leaves the viewport
      const headerHeight = (header && header.offsetHeight) || HEADER_FALLBACK;
      const headerEdge = headerHeight + APPEAR_OFFSET + (isCtaVisible ? 0 : HYSTERESIS);
      isCtaVisible = bottom > headerEdge;
      setIsVisible(!isCtaVisible);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [isDesktop, mainCtaRef, loadedProduct.code]);

  useEffect(() => {
    if (isConfirmationVisible || isNotificationModalOpen) {
      if (overlayRef.current) {
        disableBodyScroll(overlayRef.current);
      }
    } else {
      clearAllBodyScrollLocks();
    }
  }, [isNotificationModalOpen, isConfirmationVisible]);

  useEffect(() => {
    const updateFooterGutter = () => {
      if (stickyBarRef.current) {
        document.body.style.setProperty(BOTTOM_BAR_HEIGHT_VARIABLE, `${stickyBarRef.current.offsetHeight}px`);
      }
    };

    if (isVisible) {
      document.body.classList.add(BOTTOM_BAR_VISIBLE_CLASS);
      updateFooterGutter();
      window.addEventListener('resize', updateFooterGutter);
    } else {
      document.body.classList.remove(BOTTOM_BAR_VISIBLE_CLASS);
      document.body.style.removeProperty(BOTTOM_BAR_HEIGHT_VARIABLE);
    }

    return () => {
      window.removeEventListener('resize', updateFooterGutter);
      document.body.classList.remove(BOTTOM_BAR_VISIBLE_CLASS);
      document.body.style.removeProperty(BOTTOM_BAR_HEIGHT_VARIABLE);
    };
  }, [isVisible]);

  useEffect(() => {
    if (isConfirmationVisible && !overlayRef.current.classList.contains('pdp-modal__overlay--visible')) {
      setTimeout(() => {
        fadeWidget('in');
      }, 0);
    }
  }, [isConfirmationVisible]);

  useEffect(() => {
    if (isNotificationModalOpen) {
      setIsConfirmationVisible(false);
    }
  }, [isNotificationModalOpen]);

  function closeWidget() {
    fadeWidget('out');
    setTimeout(() => {
      setIsConfirmationVisible(false);
    }, fadeSpeed);
  }

  function fadeWidget(type) {
    if (backdropRef.current && overlayRef.current) {
      if (type === 'in') {
        addClass('pdp-modal__backdrop--visible', backdropRef.current);
        addClass('pdp-modal__overlay--visible', overlayRef.current);
      } else if (type === 'out') {
        removeClass('pdp-modal__overlay--visible', overlayRef.current);
        removeClass('pdp-modal__backdrop--visible', backdropRef.current);
      }
    }
  }

  if (isEmpty(loadedProduct)) {
    return null;
  }

  const baseButtonState = ATB_STATE[addToBagState] || ATB_STATE[STATUS_CODES.ADD_PRODUCT_READY];
  const selectSizeAddToBag = (
    <FormattedMessage
      id="products.pdp.addToBag.selectSizeAddToBag"
      defaultMessage="Select Size & Add to Bag"
      description="Select Size & Add to Bag"
    />
  );

  const isMultiVariant = isMultiVariantProduct(productType);
  const sizeSelected = !isEmpty(selectedSize);
  const selectedSizeOutOfStock = sizeSelected && selectedSize.count <= 0;
  const notifyMeApplicable = !disableNotifyMe;

  const showNotifyMe = (!hasStock || selectedSizeOutOfStock) && notifyMeApplicable;
  const showAddToBagDirect = sizeSelected && !selectedSizeOutOfStock && hasStock;
  // a fully sold out product keeps its own state ('Notify Me' or 'Out Of Stock') instead of the select size CTA
  const showSelectSize = isMultiVariant && hasStock && !showNotifyMe && !showAddToBagDirect;

  function onAddToBagClick() {
    addToBag().then(
      json => {
        if (json) {
          setCartData(json);
          setIsConfirmationVisible(true);
        }
      },
      () => {
        showError({
          messageId: OUT_OF_STOCK_ERROR_MESSAGE_ID,
          autoHide: true,
          sticky: true,
        });
      }
    );
  }

  function handleClick() {
    if (showNotifyMe) {
      onNotifyAction();
      return;
    }
    if (showAddToBagDirect) {
      onAddToBagClick();
      return;
    }
    if (!showSelectSize) {
      return;
    }
    if (!sizeSelected) {
      onSelectSizeRequired();
    }
    const headerEl = document.querySelector('.header');
    scrollToSizeSelector(headerEl ? headerEl.offsetHeight : 0);
  }

  const buttonState = showSelectSize
    ? {
        ...baseButtonState,
        buttonText: selectSizeAddToBag,
        icon: '_is-hidden',
        disabled: false,
        actionClassName: 'js-tracking-sticky-atb',
      }
    : baseButtonState;

  return (
    <Fragment>
      <div
        ref={stickyBarRef}
        className={`product__add-product-wrapper pdp-b${isVisible ? ' pdp-b--visible' : ''}`}
        aria-hidden={!isVisible}
      >
        <AddProductButton onClick={handleClick} buttonState={buttonState} />
      </div>
      {isConfirmationVisible && !isNotificationModalOpen && (
        <div className="pdp-modal">
          <div className="pdp-modal__backdrop" ref={backdropRef} onClick={closeWidget} role="presentation" />
          <div className="pdp-modal__overlay" ref={overlayRef}>
            <button type="button" className="pdp-modal__close" onClick={closeWidget}>
              <span className="icon-close" />
            </button>
            <ConfirmationView
              closeProductInfo={closeWidget}
              product={loadedProduct}
              addedMsgId="products.pdp.addToBag.addedToBag"
              viewAddedProductsMsgId="products.details.screen.confirmation.view.bag"
              addedProductsUrl={baseCartUrl}
              cartData={cartData}
            />
          </div>
        </div>
      )}
    </Fragment>
  );
}

StickyAddToBagWidget.defaultProps = {
  mainCtaRef: null,
  onNotifyAction: () => {},
  onSelectSizeRequired: () => {},
};

StickyAddToBagWidget.propTypes = {
  detailsPage: PropTypes.objectOf(PropTypes.any).isRequired,
  isNotificationModalOpen: PropTypes.bool.isRequired,
  mainCtaRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  onNotifyAction: PropTypes.func,
  onSelectSizeRequired: PropTypes.func,
};

const mapStateToProps = ({ detailsPage }) => ({
  detailsPage,
});

export default connect(mapStateToProps)(StickyAddToBagWidget);
